// =====================================================================
// src/screens/super-admin/lib/reports.js
//
// Admin-side read layer over V1's /reports collection (Session B).
//
// WHAT THIS IS, AND WHAT IT ISN'T
// -------------------------------
// V1 reports are member-reports-member, scoped to a group: a member taps
// "Report" on someone in their batch, and the ADMINS OF THAT BATCH resolve
// it (dismiss / warn / remove) via subscribeToGroupReports + resolveReport
// in src/lib/db.js. That flow is untouched. Resolution stays with group
// admins.
//
// This module gives the platform admin READ-ONLY oversight across every
// group. The gap it closes is concrete: if a group admin is themselves the
// bad actor, or simply never opens the moderation queue, a reported member
// has no recourse and Aangan cannot see the report at all. V1's report
// sheet promises "Admins of this batch will review within 24 hours" with
// no fallback when they don't.
//
// Deliberately NO write path. Letting a platform admin overrule a group
// admin's resolution is a moderation-policy question (what happens to the
// group admin? is the member told? can it be appealed?), not a UI one.
// Until that's decided, visibility alone is the honest feature.
//
// FIRESTORE RULES
// ---------------
// The /reports read rule (Session B) reads:
//
//     allow read: if signedIn() && (
//       resource.data.reporterId == request.auth.uid
//       || isPlatformAdmin()
//       || isGroupAdmin(resource.data.groupId)
//     );
//
// The clause ORDER matters and the rule file says so at length. Short
// version: isGroupAdmin() does an exists() on a per-group path, so on a
// cross-group admin query that's one distinct document-access per report
// and the query dies on the rules access-call budget. isPlatformAdmin()
// short-circuits first and only ever touches /users/{adminUid}, cached
// for the whole query.
//
// COST DISCIPLINE
// ---------------
// - listReportsForAdmin: exactly ADMIN_PAGE_SIZE reads per page, cursor
//   paginated. No unbounded .get().
// - Reports store UIDs, not names. Eagerly joining reporter + reported +
//   group for 25 rows would be ~75 extra reads for names the admin mostly
//   never looks at. So the list renders identifiers, and loadReportContext
//   fetches the three docs ON DEMAND when a row is opened (3 reads), which
//   the page caches per report id.
// =====================================================================

import {
  collection,
  doc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  getDoc,
} from 'firebase/firestore';
import { db } from '../../../lib/firebase';

export const ADMIN_PAGE_SIZE = 25;

// Sentinel for "no status filter" — mirrors STATUS_FILTER_ALL in
// super-admin/lib/businesses.js so the pages read alike.
export const STATUS_FILTER_ALL = null;

// Default view is the OPEN queue, not everything. The gap being closed is
// "nobody acted on this", so unresolved reports are what should greet the
// admin. Resolved history is one chip away.
export const DEFAULT_STATUS_FILTER = 'open';

// Statuses written by V1's resolveReport (src/lib/db.js):
//   dismiss -> 'dismissed' | warn -> 'warned' | remove -> 'removed'
export const STATUS_FILTER_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'warned', label: 'Warned' },
  { value: 'removed', label: 'Removed' },
  { value: 'dismissed', label: 'Dismissed' },
  { value: STATUS_FILTER_ALL, label: 'All' },
];

// Keys must match ReportUserSheet in src/App.jsx.
const REASON_LABEL = {
  // V1 member-report reasons (ReportUserSheet in src/App.jsx)
  abuse: 'Abuse or harassment',
  spam: 'Spam or scam',
  hate: 'Hate speech',
  impersonation: 'Impersonation',
  inappropriate: 'Inappropriate content',
  other: 'Something else',
  // Session C1 — business wall-post report reasons (WALL_REPORT_REASONS in
  // src/screens/v2/WallPostCard.jsx). 'inappropriate', 'impersonation',
  // 'other' overlap with V1 and reuse the labels above.
  misleading: 'Misleading or false information',
  scam: 'Scam or fraud',
  prohibited: 'Prohibited or restricted item',
  // Session C2a — order-chat report reasons (ORDER_REPORT_REASONS in
  // src/screens/v2/OrderChat.jsx) deliberately reuse existing keys only:
  // abuse, spam, scam, inappropriate, impersonation, other. No new labels
  // are needed — a third vocabulary for the same handful of concepts would
  // just be three ways to say "spam" in one admin table.
};

export function labelForReason(key) {
  return REASON_LABEL[key] || key || '—';
}

export function labelForReportStatus(status) {
  if (status === 'open') return 'Open';
  if (status === 'warned') return 'Warned';
  if (status === 'removed') return 'Removed';
  if (status === 'dismissed') return 'Dismissed';
  return status || '—';
}

// Reuses the four pill variants already defined for BusinessesPage rather
// than inventing new ones — same visual language across the panel.
//   open      → pending  (amber: needs attention)
//   warned    → suspended
//   removed   → rejected (red: member was removed from the batch)
//   dismissed → active   (green: reviewed, no action needed)
export function pillClassForReportStatus(status) {
  if (status === 'open') return 'sa-pill-pending';
  if (status === 'warned') return 'sa-pill-suspended';
  if (status === 'removed') return 'sa-pill-rejected';
  if (status === 'dismissed') return 'sa-pill-active';
  return 'sa-pill-suspended';
}

// ============================================================
// ageInDays(ts) / isStale(report)
// ============================================================
//
// V1's report sheet tells the reporter "Admins of this batch will review
// within 24 hours." An OPEN report older than STALE_AFTER_HOURS is one
// nobody kept that promise on — precisely the signal this page exists to
// surface. Resolved reports are never stale, however old.
export const STALE_AFTER_HOURS = 72;

export function ageInHours(ts) {
  if (!ts) return null;
  let ms;
  if (typeof ts?.toMillis === 'function') ms = ts.toMillis();
  else if (ts instanceof Date) ms = ts.getTime();
  else if (typeof ts?.seconds === 'number') ms = ts.seconds * 1000;
  else return null;
  return (Date.now() - ms) / (1000 * 60 * 60);
}

export function isStale(report) {
  if (!report || report.status !== 'open') return false;
  const hrs = ageInHours(report.createdAt);
  return hrs != null && hrs > STALE_AFTER_HOURS;
}

// Human "3d" / "5h" / "just now" for the age column.
export function shortAge(ts) {
  const hrs = ageInHours(ts);
  if (hrs == null) return '';
  if (hrs < 1) return 'just now';
  if (hrs < 24) return `${Math.floor(hrs)}h`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d`;
  const months = Math.floor(days / 30);
  return `${months}mo`;
}

// ============================================================
// listReportsForAdmin({ statusFilter, cursor })
// ============================================================
//
// Cross-group, newest first. Platform-admin only — the rules fail closed
// for everyone else.
//
// Args:
//   statusFilter (string | null) — one of STATUS_FILTER_OPTIONS values.
//     null means no filter (the "All" chip).
//   cursor (DocumentSnapshot | null) — lastDoc from the previous page.
//
// Returns: { reports, lastDoc, hasMore }
//
// Firestore cost: exactly ADMIN_PAGE_SIZE reads (fewer on the last page).
//
// Required indexes (both in firestore.indexes.json):
//   - reports (status ASC, createdAt DESC)  ← the filtered queries
//   - the unfiltered orderBy(createdAt desc) needs only the automatic
//     single-field index.
export async function listReportsForAdmin({
  statusFilter = DEFAULT_STATUS_FILTER,
  cursor = null,
} = {}) {
  const constraints = [];
  if (statusFilter) constraints.push(where('status', '==', statusFilter));
  constraints.push(orderBy('createdAt', 'desc'));
  if (cursor) constraints.push(startAfter(cursor));
  constraints.push(limit(ADMIN_PAGE_SIZE));

  const q = query(collection(db, 'reports'), ...constraints);
  const snap = await getDocs(q);

  const reports = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  const lastDoc = snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null;
  const hasMore = snap.docs.length === ADMIN_PAGE_SIZE;

  return { reports, lastDoc, hasMore };
}

// Best-effort single doc read. Returns null rather than throwing, because
// a missing reported user or a deleted group must not blank the drawer —
// the report itself is still the thing the admin came to read.
async function safeGet(path, id) {
  if (!id) return null;
  try {
    const snap = await getDoc(doc(db, path, id));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  } catch (err) {
    console.warn(`[reports] could not read ${path}/${id}:`, err?.code || err);
    return null;
  }
}

// ============================================================
// loadReportContext(report)
// ============================================================
//
// Resolves the three UIDs a report carries into displayable docs. Called
// ONLY when the admin opens a row, never for the list.
//
// Both /users and /groups are `allow read: if signedIn()`, so no extra
// rules work was needed for this.
//
// Firestore cost: 3 reads, in parallel.
export async function loadReportContext(report) {
  if (!report) return { reporter: null, reported: null, group: null, business: null };

  // Session C1 — a business_post report has no reportedId/groupId. It carries
  // businessId (+ targetId, the post id). Resolve the reporter (a user) and
  // the business instead. Detecting via targetType keeps V1 member reports on
  // their original 3-doc path.
  if (report.targetType === 'business_post') {
    const [reporter, business] = await Promise.all([
      safeGet('users', report.reporterId),
      safeGet('businesses', report.businessId),
    ]);
    return { reporter, reported: null, group: null, business, order: null };
  }

  // Session C2a — an order_chat report has a reporter, a counterparty
  // (reportedUserId), a business, and an order. 4 reads, in parallel. The
  // order doc is what makes the report actionable: it carries the status,
  // total and items the two of them were arguing about.
  if (report.targetType === 'order_chat') {
    const [reporter, reported, business, order] = await Promise.all([
      safeGet('users', report.reporterId),
      safeGet('users', report.reportedUserId),
      safeGet('businesses', report.businessId),
      safeGet('orders', report.orderId),
    ]);
    return { reporter, reported, group: null, business, order };
  }

  const [reporter, reported, group] = await Promise.all([
    safeGet('users', report.reporterId),
    safeGet('users', report.reportedId),
    safeGet('groups', report.groupId),
  ]);
  return { reporter, reported, group, business: null, order: null };
}

// Session C1 — is this a business wall-post report (vs a V1 member report)?
export function isBusinessReport(report) {
  return report?.targetType === 'business_post';
}

// Session C2a — is this an order-chat report?
export function isOrderReport(report) {
  return report?.targetType === 'order_chat';
}

// True for any V2 report shape (business_post | order_chat). These share
// two traits the admin UI cares about: they have no batch, and they have no
// resolve path (firestore.rules gates update on isGroupAdmin(groupId), which
// V2 reports don't carry). Use this rather than !isMemberReport so a fourth
// shape doesn't silently inherit the V1 rendering.
export function isPlatformReport(report) {
  return isBusinessReport(report) || isOrderReport(report);
}
